// A Boo whose brain can't be reached: the page says so once (badge, banner),
// greys out the tabs that need the brain, and keeps the rest working.
// The backend works out the state on GET /api/containers/:id/health. Kept free
// of the `@/lib/api` import chain so it can be unit-tested under `node:test`.

export type BrainState = "ok" | "starting" | "offline";
export type BrainStatus = { state: BrainState; lastSeenAt: string | null };

// Tabs whose main content comes from the brain (checked against the backend
// routes each one loads). Usage loads its total spend without it; its
// brain-backed cards say so on their own.
const BRAIN_TABS = new Set(["channels", "people", "apps", "models", "vault", "extras", "chat"]);

export function needsBrain(tab: string): boolean {
  return BRAIN_TABS.has(tab);
}

function isState(v: unknown): v is BrainState {
  return v === "ok" || v === "starting" || v === "offline";
}

// The health answer: data on a 200, the error's body on a 503. An older
// backend sends only { reachable }, read as offline with no last-seen.
export function brainStatusOf(data: unknown, error: unknown): BrainStatus | null {
  const body = (data ?? (error as { body?: unknown } | null)?.body) as
    | { reachable?: unknown; state?: unknown; lastSeenAt?: unknown }
    | undefined;
  if (!body) return error ? { state: "offline", lastSeenAt: null } : null;
  const state = isState(body.state) ? body.state : body.reachable === true ? "ok" : "offline";
  return { state, lastSeenAt: typeof body.lastSeenAt === "string" ? body.lastSeenAt : null };
}

// How the Boo page checks the brain: quietly every 30s, every 15s while down,
// one retry per check. A self-hosted Boo runs on the org's own machine: once
// per visit and when someone asks, never on a timer.
export function healthPolling(o: { selfHosted: boolean; down: boolean }): {
  retry: number | false;
  refetchInterval: number | false;
  refetchOnWindowFocus: boolean;
} {
  if (o.selfHosted) return { retry: false, refetchInterval: false, refetchOnWindowFocus: false };
  return { retry: 1, refetchInterval: o.down ? 15_000 : 30_000, refetchOnWindowFocus: true };
}

// Misses in a row before a Boo that answered during this visit counts as down,
// so one blip that recovers on the next poll never flips the page.
const DOWN_AFTER_FAILURES = 3;

export function brainDown(
  status: BrainStatus | null,
  o: { failures: number; answeredThisVisit: boolean },
): boolean {
  if (!status || status.state === "ok") return false;
  return !o.answeredThisVisit || o.failures >= DOWN_AFTER_FAILURES;
}

export function downBanner(
  state: Exclude<BrainState, "ok">,
  name: string,
  opts: { selfHosted?: boolean } = {},
): { title: string; body: string } {
  if (opts.selfHosted) {
    return {
      title: `${name} can't be reached`,
      body: "It runs on your own machine. Check that it's running and that Boo Lab has its address, then check again.",
    };
  }
  return state === "starting"
    ? {
        title: `${name} is starting up`,
        body: "This usually takes 2 to 5 minutes. You can connect Slack and set up integrations now. The rest opens when it's ready.",
      }
    : {
        title: `${name} can't be reached right now`,
        body: "We're working on it. This page updates on its own when it's back.",
      };
}

export function tabTooltip(state: Exclude<BrainState, "ok">, name: string): string {
  return state === "starting" ? `Opens when ${name} is ready` : `Available when ${name} is back`;
}

function ago(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

function since(iso: string, now: number): string {
  const m = Math.floor((now - Date.parse(iso)) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return ago(m, "minute");
  const h = Math.floor(m / 60);
  if (h < 24) return ago(h, "hour");
  return ago(Math.floor(h / 24), "day");
}

export function lastAnsweredLabel(lastSeenAt: string | null, now: number = Date.now()): string | null {
  return lastSeenAt ? `Last answered ${since(lastSeenAt, now)}` : null;
}

export function createdLabel(createdAt: string, now: number = Date.now()): string {
  return `Created ${since(createdAt, now)}`;
}

// What each brain tab holds, as the landing panel names it.
const TAB_NOUN: Record<string, string> = {
  channels: "Channels",
  people: "People settings",
  apps: "Apps",
  models: "Model choices",
  vault: "Vault entries",
  extras: "Extras",
  chat: "Chats",
};

export function landingCopy(
  tab: string,
  state: Exclude<BrainState, "ok">,
  name: string,
): { title: string | null; body: string } {
  const noun = TAB_NOUN[tab] ?? "These settings";
  if (state === "starting") {
    return {
      title: null,
      body: `Your ${noun.toLowerCase()} will appear here as soon as ${name} finishes starting. No need to refresh.`,
    };
  }
  return {
    title: `${noun} come from ${name} itself`,
    body: "They'll show here as soon as it's reachable again. Nothing you set earlier is lost.",
  };
}

// A rough bar for "2 to 5 minutes": 5% at once, 90% at five minutes, held there.
export function startingProgress(since: number, now: number = Date.now()): number {
  const pct = 5 + ((now - since) / 300_000) * 85;
  return Math.round(Math.min(90, Math.max(5, pct)));
}
